export type SharedSchedulePeriod = {
  start: string;
  end: string;
};

export type SharedScheduleDay = {
  day: string;
  label: string;
  closed: boolean;
  periods: SharedSchedulePeriod[];
};

export type WeeklyScheduleIssue = {
  key: string;
  message: string;
};

const DAY_END_TIME = '23:59';
const DAY_START_TIME = '00:00';

const normalizeTime = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (/^\d{2}:\d{2}$/.test(raw)) return raw;
  if (/^\d{2}:\d{2}:\d{2}$/.test(raw)) return raw.slice(0, 5);
  return null;
};

export const scheduleTimeToMinutes = (value: unknown): number | null => {
  const normalized = normalizeTime(value);
  if (!normalized) return null;

  const [hoursRaw, minutesRaw] = normalized.split(':');
  const hours = Number(hoursRaw);
  const minutes = Number(minutesRaw);

  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;

  return (hours * 60) + minutes;
};

const sortPeriods = (periods: SharedSchedulePeriod[]): SharedSchedulePeriod[] => (
  periods
    .slice()
    .sort((a, b) => {
      const startA = scheduleTimeToMinutes(a.start) ?? 0;
      const startB = scheduleTimeToMinutes(b.start) ?? 0;
      if (startA !== startB) return startA - startB;

      const endA = scheduleTimeToMinutes(a.end) ?? 0;
      const endB = scheduleTimeToMinutes(b.end) ?? 0;
      return endA - endB;
    })
);

const cloneDays = (days: SharedScheduleDay[]): SharedScheduleDay[] => (
  (Array.isArray(days) ? days : []).map((day) => ({
    day: String(day?.day ?? ''),
    label: String(day?.label ?? ''),
    closed: Boolean(day?.closed),
    periods: Array.isArray(day?.periods)
      ? day.periods.map((period) => ({
          start: String(period?.start ?? '').trim(),
          end: String(period?.end ?? '').trim(),
        }))
      : [],
  }))
);

export const isOvernightSchedulePeriod = (period: SharedSchedulePeriod): boolean => {
  const startMinutes = scheduleTimeToMinutes(period?.start);
  const endMinutes = scheduleTimeToMinutes(period?.end);

  return startMinutes !== null && endMinutes !== null && endMinutes < startMinutes;
};

export const expandWeeklyScheduleOvernights = (days: SharedScheduleDay[]): SharedScheduleDay[] => {
  const baseDays = cloneDays(days);
  const expandedDays = baseDays.map((day) => ({
    ...day,
    closed: true,
    periods: [] as SharedSchedulePeriod[],
  }));

  baseDays.forEach((day, dayIndex) => {
    if (day.closed) return;

    (Array.isArray(day.periods) ? day.periods : []).forEach((period) => {
      const start = normalizeTime(period?.start);
      const end = normalizeTime(period?.end);
      const startMinutes = scheduleTimeToMinutes(start);
      const endMinutes = scheduleTimeToMinutes(end);

      if (
        start === null || end === null
        || startMinutes === null || endMinutes === null
        || startMinutes === endMinutes
      ) {
        return;
      }

      if (startMinutes < endMinutes) {
        expandedDays[dayIndex].periods.push({ start, end });
        expandedDays[dayIndex].closed = false;
        return;
      }

      expandedDays[dayIndex].periods.push({ start, end: DAY_END_TIME });
      expandedDays[dayIndex].closed = false;

      if (end !== DAY_START_TIME) {
        const nextDayIndex = (dayIndex + 1) % expandedDays.length;
        expandedDays[nextDayIndex].periods.push({ start: DAY_START_TIME, end });
        expandedDays[nextDayIndex].closed = false;
      }
    });
  });

  return expandedDays.map((day) => ({
    ...day,
    closed: day.periods.length === 0,
    periods: sortPeriods(day.periods),
  }));
};

export const validateWeeklySchedule = (days: SharedScheduleDay[]): {
  expandedDays: SharedScheduleDay[];
  issues: WeeklyScheduleIssue[];
} => {
  const baseDays = cloneDays(days);
  const issues: WeeklyScheduleIssue[] = [];

  const pushIssue = (key: string, message: string) => {
    if (issues.some((issue) => issue.key === key)) return;
    issues.push({ key, message });
  };

  const hasOpenDay = baseDays.some((day) => !day.closed);
  if (!hasOpenDay) {
    pushIssue('all_closed', 'Defina pelo menos 1 dia/horário aberto.');
  }

  baseDays.forEach((day) => {
    if (day.closed) return;

    const periods = Array.isArray(day.periods) ? day.periods : [];
    if (periods.length === 0) {
      pushIssue(
        `missing_${day.day}`,
        `Informe ao menos um período válido para ${day.label}.`,
      );
      return;
    }

    periods.forEach((period, periodIndex) => {
      const start = normalizeTime(period?.start);
      const end = normalizeTime(period?.end);

      if (!start || !end) {
        pushIssue(
          `invalid_${day.day}_${periodIndex}`,
          `Período inválido em ${day.label}. Use horário no formato HH:MM.`,
        );
        return;
      }

      const startMinutes = scheduleTimeToMinutes(start);
      const endMinutes = scheduleTimeToMinutes(end);

      if (startMinutes === null || endMinutes === null) {
        pushIssue(
          `invalid_${day.day}_${periodIndex}`,
          `Período inválido em ${day.label}. Use horário no formato HH:MM.`,
        );
        return;
      }

      if (startMinutes === endMinutes) {
        pushIssue(
          `same_time_${day.day}_${periodIndex}`,
          `Período inválido em ${day.label}. A abertura precisa ser diferente do fechamento.`,
        );
      }
    });
  });

  const expandedDays = expandWeeklyScheduleOvernights(baseDays);

  expandedDays.forEach((day) => {
    if (day.closed) return;

    const sorted = sortPeriods(day.periods);
    for (let index = 1; index < sorted.length; index += 1) {
      const previous = sorted[index - 1];
      const current = sorted[index];
      const previousEnd = scheduleTimeToMinutes(previous.end);
      const currentStart = scheduleTimeToMinutes(current.start);

      if (previousEnd !== null && currentStart !== null && currentStart < previousEnd) {
        pushIssue(
          `overlap_${day.day}`,
          `Há períodos sobrepostos em ${day.label}.`,
        );
        return;
      }
    }
  });

  return { expandedDays, issues };
};
